import React, { useState, useEffect } from 'react';
import * as dates from '@simplybenefits/sb-dates';
import EmployeeDetailsModal from './EmployeeDetailsModal/EmployeeDetailsModal.js';
import { parseUserDataForAddUpdate } from './EmployeeDetailsModal/parseEmployeeData';
import EffectiveDateModal from './EffectiveDateModal.js';
import TerminateEmployeeModal from './TerminateEmployeeModal.js';
import { validateInput } from '../../services/index.js';
import i18n from '../../localization/i18n.js';
import tokens from '../../localization/tokens.js';

/**
 * 
 * @param {*} param0 
 * @returns 
 */
const EmployeeModalHandler = ({
  open,
  isEdit = false,
  close,
  currentEmployee,
  classData,
  divisions,
  currentAccountMeta,
  addUser,
  updateUser,
  isMasterSite,
  isMasterProfile = false,
  showTerminateMemberButton = false,
  removeUser,
  loader
}) => {
  const [employeeDetailsModalOpen, setEmployeeDetailsModalOpen] = useState(open);
  const [user, setUser] = useState(null);
  const [effectiveDate, setEffectiveDate] = useState(() => dates.formatIncomingDate(dates.getUTC()));
  const [effectiveDateError, setEffectiveDateError] = useState(false);
  const [expectedLeaveReturnDate, setExpectedLeaveReturnDate] = useState(() => dates.formatIncomingDate(dates.getUTC()));
  const [expectedLeaveReturnDateError, setExpectedLeaveReturnDateError] = useState(false);
  const [effectiveDateModalOpen, setEffectiveDateModalOpen] = useState(false);
  const [showTerminateModal, setShowTerminationModal] = useState(false);
  const isStudent = currentAccountMeta?.isStudentGroup;
  const isAccountPending = Number(currentAccountMeta?.status) === 0;
  const isAccountInRevisions = Number(currentAccountMeta?.status) === 3;
  useEffect(() => {
    setEmployeeDetailsModalOpen(open);
  }, [open, currentEmployee]);

  /**
   * Validates the effective date based on various conditions.
   *
   * @returns {Promise<boolean>} True if the effective date is valid, false otherwise.
   */
  const _validateEffectiveDate = async () => {
    let effectiveDateValidation = await validateInput('effectiveDate', effectiveDate, i18n.locale);
    const startOfMonth = dates.getStartOfMonth();
    const effectiveDateUTC = dates.getUTC(effectiveDate);

    // agents can make backdated changes further than one month in the past, admins and advisors are restricted to one month
    if (effectiveDateUTC < startOfMonth && !isMasterSite) {
      effectiveDateValidation = i18n.t(tokens.labels.employees.backdatedChanges);
    }
    setEffectiveDateError(effectiveDateValidation);

    // if we are editing the user from the master profile, we need to validate the return date
    let expectedLeaveReturnDateValidation = false;
    if (isMasterProfile && currentEmployee.status !== user.status && ['Parental Leave', 'Leave of Absence', 'Temporary Lay Off', 'Disability', 'Strike', 'Approved Leave'].includes(user.status)) {
      expectedLeaveReturnDateValidation = await validateInput('effectiveDate', expectedLeaveReturnDate);
      setExpectedLeaveReturnDateError(expectedLeaveReturnDateValidation);
    }
    if (effectiveDateValidation || expectedLeaveReturnDateValidation) {
      return false;
    }
    return true;
  };

  /**
   * Returns the effective date based on account status and student/non-student status.
   *
   * @returns {Date} The effective date.
   */
  const returnEffectiveDate = userData => {
    if (isAccountPending || isAccountInRevisions) {
      if (isStudent) {
        return userData.studyStartDate;
      }
      return userData.hireDate;
    }
    return dates.getUTC(effectiveDate);
  };

  /**
   * Updates user data with the effective date after validation.
   *
   * @returns {Promise<boolean>} True if the update was successful, false otherwise.
   */
  const _updateUser = async userData => {
    // we do not need to validate the effective date if the account is pending or in revisions, we will use the users hire date as the effective date of the change
    if (!isAccountPending && !isAccountInRevisions) {
      const effectiveDateValid = await _validateEffectiveDate();
      if (!effectiveDateValid) {
        return false;
      }
    }

    /*
     * if the account is pending and we are on the advisor site, we can pass the userData in directly from the modal
     * otherwise, we need to set the user data in state and open the effective date modal to get the effective date of the update
     */
    const userUpdateData = parseUserDataForAddUpdate(userData, classData, divisions, isStudent, isMasterSite, isEdit, isMasterProfile);
    const userDataWithEffectiveDate = {
      ...userUpdateData,
      effectiveDate: returnEffectiveDate(userUpdateData),
      // if we are editing from the master profile, send the expected return date if we have it
      ...(isMasterProfile ? {
        expectedLeaveReturnDate: expectedLeaveReturnDate ? dates.getUTC(expectedLeaveReturnDate) : null
      } : {})
    };

    // if we are editing from the master profile, we need to use a different endpoint
    const success = isMasterProfile ? await updateUser(currentEmployee.userID, userDataWithEffectiveDate, true) : await updateUser(userDataWithEffectiveDate, currentEmployee.userID, currentAccountMeta.accountID);
    if (success) {
      close();
      return true;
    }
    return false;
  };

  /**
   * Handles saving employee data, either directly or by opening the effective date modal.
   *
   * @param {object} userData - User data to be saved.
   * @returns {boolean} True if the data was saved successfully, false otherwise.
   */
  const saveEmployeeData = userData => {
    setUser(userData);

    // if the account is not yet setup then we don't need to ask for an effective date
    if (isAccountPending || isAccountInRevisions) {
      if (isEdit) {
        return _updateUser(userData);
      }
      return true;
    }
    setEmployeeDetailsModalOpen(false);
    setEffectiveDateModalOpen(true);
    return true;
  };

  /**
   * 
   * @param {*} user 
   * @returns 
   */
  const _addUser = async userData => {
    const dataForUpdate = parseUserDataForAddUpdate(userData, classData, divisions, isStudent, isMasterSite);
    const success = await addUser(dataForUpdate, currentAccountMeta.accountID);
    if (success) {
      close();
      return true;
    }
    return false;
  };
  if (effectiveDateModalOpen) {
    return /*#__PURE__*/React.createElement(EffectiveDateModal, {
      open: true,
      onClick: () => _updateUser(user),
      setEffectiveDate: setEffectiveDate,
      effectiveDate: effectiveDate,
      effectiveDateError: effectiveDateError,
      onClose: () => {
        setEffectiveDateModalOpen(false);
        setEmployeeDetailsModalOpen(true);
      },
      isMasterProfile: isMasterProfile,
      employeeMeta: {
        ...currentEmployee,
        ...user
      },
      setExpectedLeaveReturnDate: setExpectedLeaveReturnDate,
      expectedLeaveReturnDate: expectedLeaveReturnDate,
      expectedLeaveReturnDateError: expectedLeaveReturnDateError
    });
  }
  if (showTerminateModal) {
    return /*#__PURE__*/React.createElement(TerminateEmployeeModal, {
      open: true,
      close: () => setShowTerminationModal(false),
      currentEmployee: currentEmployee,
      terminate: removeUser,
      loader: loader
    });
  }
  return /*#__PURE__*/React.createElement(EmployeeDetailsModal, {
    open: employeeDetailsModalOpen,
    isEdit: isEdit,
    close: close,
    currentEmployee: currentEmployee,
    classData: classData,
    divisions: divisions,
    currentAccountMeta: currentAccountMeta,
    addUser: _addUser,
    updateUser: saveEmployeeData,
    isAdmin: false,
    loader: loader,
    showTerminateMemberButton: showTerminateMemberButton,
    setShowTerminationModal: setShowTerminationModal,
    isMasterProfile: isMasterProfile,
    isMasterSite: isMasterSite
  });
};
export default EmployeeModalHandler;